import React, {
  useMemo,
  useState,
  useRef,
  useEffect,
  useCallback,
} from 'react';
import PropTypes from 'prop-types';
import AddClientAuthModal from './AddClientAuthModal';
import EditClientAuthModal from './EditClientAuthModal';
import {
  MotifFormField,
  MotifSearch,
  MotifButton,
  MotifTable,
} from '@ey-xd/motif-react';
import {
  handleFilterChanged,
  handleFirstDataRendered,
  handleGridReady,
  handleItemsPerPageChange,
} from '../../common/AgGridEventHandlers';
import CustomPagination from '../../common/MotifPagination';
import AuthCellRenderer from './customRenderer/AuthCellRenderer';
import CustomHeaderTemplate from '../shared/customHeaders/CustomHeaderTemplate';

const ClientAuthList = ({
  auths,
  handleSearchChange,
  handleClearSearchText,
  clientUserSearchText,
  handleEditButtonClick,
  handleDeleteButtonClick,
  handleNewButtonClick,
  showAddClientAuthModal,
  handleAddClientAuthModalAction,
  auth,
  toggleShowAddClientAuthModal,
  showEditClientAuthModal,
  handleEditClientAuthModalAction,
  toggleShowEditClientAuthModal,
  addAuthMessageText,
  maxLimitMessageText,
  validateDomainWithRole,
  clientAuthFormValues,
  handleAddClientAuthModalValidateAction,
  validatingUser,
  myEyError,
  handleDailyNewsletter,
  receiveDailyNewsletter,
  receiveWeeklyNewsletter,
  handleWeeklyNewsletter,
  receiveRealTimeNotification,
  handleRealTimeNotification,
  receiveRealTimeNotificationForNewPsqArqcSent,
  handleInternalReviewNotification,
  handleOnRoleTypeChange,
  handleGridChanges,
  isDataSteward,
  handleDataSteward,
  dataStewardCheckboxDisplay,
  showDSError,
  dataStewardValue,
}) => {
  const gridRef = useRef();
  const [rowData, setRowData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(100);

  //Default column settings
  const defaultColDef = useMemo(() => {
    return {
      flex: 1,
      minWidth: 65,
      filter: true,
      resizable: true,
      sortable: true,
      wrapText: true,
      autoHeight: true,
      wrapHeaderText: true,
      autoHeaderHeight: true,
    };
  }, []);

  const getRoleName = useCallback(
    (role) => {
      if (role === 1) {
        if (auth.resource === 1) {
          role = 'System Administrator';
        } else {
          role = 'Engagement Administrator';
        }
      } else if (role == 4) {
        role = 'External Checklist User';
      } else {
        role = 'User';
      }
      return role;
    },
    [auth.resource]
  );

  const columnDefs = useMemo(
    () => [
      {
        field: 'userId',
        headerName: 'User',
        sortable: true,
        showRowGroup: false,
        rowGroup: false,
        minWidth: 350,
        comparator: (a, b) => {
          var valA = a.toString().toLowerCase();
          var valB = b.toString().toLowerCase();
          if (valA === valB) return 0;
          return valA > valB ? 1 : -1;
        },
        headerComponentParams: { template: CustomHeaderTemplate('User') },
      },
      {
        field: 'userId',
        headerName: 'Role',
        sortable: true,
        showRowGroup: false,
        hide: false,
        rowGroup: false,
        minWidth: 120,
        valueGetter: (params) => {
          return getRoleName(params.data.role);
        },
        wrapText: true,
        headerComponentParams: { template: CustomHeaderTemplate('Role') },
      },
      {
        field: 'isDataSteward',
        headerName: 'Data Steward',
        sortable: true,
        showRowGroup: false,
        rowGroup: false,
        minWidth: 110,
        valueGetter: (params) => {
          const value = params.data.isDataSteward ? 'Yes' : 'No';
          return value;
        },
        headerComponentParams: {
          template: CustomHeaderTemplate('DataSteward'),
        },
      },
      {
        field: 'receiveDailyNewsletter',
        headerName: 'Daily Newsletter',
        sortable: true,
        showRowGroup: false,
        hide: false,
        rowGroup: false,
        minWidth: 155,
        valueGetter: (params) => {
          const value = params.data.receiveDailyNewsletter ? 'Yes' : 'No';
          return value;
        },
        headerComponentParams: {
          template: CustomHeaderTemplate('DailyNewsletter'),
        },
      },
      {
        field: 'receiveWeeklyNewsletter',
        headerName: 'Weekly Newsletter',
        sortable: true,
        showRowGroup: false,
        hide: false,
        rowGroup: false,
        minWidth: 155,
        valueGetter: (params) => {
          const value = params.data.receiveWeeklyNewsletter ? 'Yes' : 'No';
          return value;
        },
        headerComponentParams: {
          template: CustomHeaderTemplate('WeeklyNewsletter'),
        },
      },
      {
        field: 'receiveRealTimeNotification',
        headerName: 'Reopen Submitted or Closed Checklist',
        sortable: true,
        showRowGroup: false,
        hide: false,
        rowGroup: false,
        minWidth: 230,
        valueGetter: (params) => {
          const value = params.data.receiveRealTimeNotification ? 'Yes' : 'No';
          return value;
        },
        headerComponentParams: {
          template: CustomHeaderTemplate('ReopenSubmittedorClosedChecklist'),
        },
      },

      {
        field: 'receiveRealTimeNotificationForNewPsqArqcSent',
        headerName: 'PSQ/ARQC Sent/Internal Review Notification',
        sortable: true,
        showRowGroup: false,
        hide: false,
        rowGroup: false,
        minWidth: 190,
        valueGetter: (params) => {
          const value = params.data.receiveRealTimeNotificationForNewPsqArqcSent
            ? 'Yes'
            : 'No';
          return value;
        },
        headerComponentParams: {
          template: CustomHeaderTemplate('PsqArqcReviewNotification'),
        },
      },
      {
        field: 'editactions',
        headerName: '',
        suppressMenu: true,
        suppressHeaderFilterButton: true,
        suppressSeparator: true,
        sortable: false,
        minWidth: 70,
        maxWidth: 70,
        cellRenderer: AuthCellRenderer,
        cellRendererParams: { handleEditButtonClick },
      },
      {
        field: 'deleteactions',
        headerName: '',
        suppressMenu: true,
        suppressHeaderFilterButton: true,
        suppressSeparator: true,
        sortable: false,
        minWidth: 70,
        maxWidth: 70,
        cellRenderer: AuthCellRenderer,
        cellRendererParams: { handleDeleteButtonClick },
      },
    ],
    [handleEditButtonClick, handleDeleteButtonClick, getRoleName]
  );

  //Handle deselect all event
  const handleDeselectAll = useCallback((val) => {
    // set current page and total pages to 0
    setCurrentPage(0);
    setTotalPages(0);
  }, []);

  //Handle Grid Page change event
  const handlePageChange = useCallback((val) => {
    setCurrentPage(val);
    gridRef.current.api.paginationGoToPage(val - 1);
  }, []);

  useEffect(() => {
    handleGridChanges(
      rowData,
      gridRef && gridRef.current ? gridRef.current.api : null
    );
    if (rowData.length > 0 && gridRef?.current?.api !== undefined) {
      let calcTotalPages = gridRef.current.api.paginationGetTotalPages();
      setTotalPages(calcTotalPages);
      //If total page is zero then set the current page as zero
      if (calcTotalPages == 0) {
        setCurrentPage(calcTotalPages);
      }
    }
  }, [rowData]);

  useEffect(() => {
    setRowData(auths);
    if (gridRef && gridRef.current && gridRef.current.api) {
      // Get total pages from Grid's default pagination control and apply it to custom pagination control
      let calcTotalPages = gridRef.current.api.paginationGetTotalPages();
      setTotalPages(calcTotalPages);

      gridRef.current.api.redrawRows();
    }
  }, [auths]);

  const onCellKeyDown = useCallback(
    (params) => {
      if (params.event.key === 'Enter' || params.event.key === ' ') {
        if (params.colDef.field == 'editactions') {
          handleEditButtonClick(params.data);
        }
        if (params.colDef.field == 'deleteactions') {
          handleDeleteButtonClick(params.data);
        }
      }
    },
    [handleEditButtonClick, handleDeleteButtonClick]
  );

  return (
    <section>
      <div className="row">
        <div className="col-md-6"></div>
        <div className="col-md-6 d-flex justify-content-end align-items-center">
          <MotifFormField className="mr-2">
            <MotifSearch
              type="text"
              onChange={handleSearchChange}
              value={clientUserSearchText}
              onClear={handleClearSearchText}
              title="Type in a user name"
              aria-label="Search for User"
              placeholder="Search for User"
              searchButtonProps={{ tabIndex: '-1' }}
              data-testid="txtSearchForUser"
            />
          </MotifFormField>
          <MotifButton
            variant="primary"
            type="button"
            data-testid="btnAddAuthorizedUser"
            onClick={handleNewButtonClick}
          >
            Add Authorized User
          </MotifButton>
        </div>
      </div>
      <React.Fragment>
        <div className="mt-2">
          {rowData.length > 0 && (
            <div>
              <div>
                <div className="row">
                  <div className="col">
                    <div className="ag-theme-quartz motif-table table-bd-full-height-width">
                      <MotifTable
                        zebra={true}
                        compact={true}
                        rowData={rowData}
                        columnDefs={columnDefs}
                        defaultColDef={defaultColDef}
                        accentedSort={true}
                        rowSelection="multiple"
                        suppressRowClickSelection={true}
                        onGridReady={(event) =>
                          handleGridReady(
                            event,
                            gridRef,
                            setCurrentPage,
                            setItemsPerPage,
                            setTotalPages,
                            'REITSuiteClientListColumnState',
                            handleDeselectAll
                          )
                        }
                        onFirstDataRendered={handleFirstDataRendered(gridRef)}
                        ref={gridRef}
                        pagination={true}
                        suppressPaginationPanel={true}
                        paginationPageSize={100}
                        groupDisplayType={'custom'}
                        groupSelectsChildren={true}
                        groupDefaultExpanded={0}
                        onFilterChanged={handleFilterChanged(
                          gridRef,
                          currentPage,
                          setCurrentPage,
                          setTotalPages
                        )}
                        data-testid="grdAuthorizedUsersData"
                        onCellKeyDown={onCellKeyDown}
                      />

                      <div className="mt-3">
                        <span className="right mb-3">
                          <CustomPagination
                            currentPage={currentPage}
                            handlePageChange={handlePageChange}
                            totalPages={totalPages}
                            handleItemsPerPageChange={handleItemsPerPageChange}
                            gridRef={gridRef}
                            setCurrentPage={setCurrentPage}
                            setItemsPerPage={setItemsPerPage}
                            setTotalPages={setTotalPages}
                          />
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
          {(auths &&
            auths.length === 0 &&
            (clientUserSearchText === undefined ||
              clientUserSearchText === '') && (
              <div>
                <h5>No authorized user has been added for this client.</h5>
              </div>
            )) ||
            (auths && auths.length === 0 && clientUserSearchText !== '' && (
              <div>
                <h5>
                  No authorized user match your search text &apos;
                  {clientUserSearchText}&apos;
                </h5>
              </div>
            )) ||
            null}
        </div>
      </React.Fragment>
      <AddClientAuthModal
        showAddClientAuthModal={showAddClientAuthModal}
        onSubmit={handleAddClientAuthModalAction}
        toggleShowAddClientAuthModal={toggleShowAddClientAuthModal}
        initialValues={auth}
        addAuthMessageText={addAuthMessageText}
        maxLimitMessageText={maxLimitMessageText}
        validateDomainWithRole={validateDomainWithRole}
        clientAuthFormValues={clientAuthFormValues}
        handleAddClientAuthModalValidateAction={
          handleAddClientAuthModalValidateAction
        }
        validatingUser={validatingUser}
        myEyError={myEyError}
        handleDailyNewsletter={handleDailyNewsletter}
        receiveDailyNewsletter={receiveDailyNewsletter}
        handleWeeklyNewsletter={handleWeeklyNewsletter}
        receiveWeeklyNewsletter={receiveWeeklyNewsletter}
        handleRealTimeNotification={handleRealTimeNotification}
        receiveRealTimeNotification={receiveRealTimeNotification}
        receiveRealTimeNotificationForNewPsqArqcSent={
          receiveRealTimeNotificationForNewPsqArqcSent
        }
        handleInternalReviewNotification={handleInternalReviewNotification}
        handleOnRoleTypeChange={handleOnRoleTypeChange}
        isDataSteward={isDataSteward}
        handleDataSteward={handleDataSteward}
        dataStewardCheckboxDisplay={dataStewardCheckboxDisplay}
        showDSError={showDSError}
      />
      <EditClientAuthModal
        showEditClientAuthModal={showEditClientAuthModal}
        onSubmit={handleEditClientAuthModalAction}
        toggleShowEditClientAuthModal={toggleShowEditClientAuthModal}
        initialValues={auth}
        clientAuthFormValues={clientAuthFormValues}
        handleDailyNewsletter={handleDailyNewsletter}
        receiveDailyNewsletter={receiveDailyNewsletter}
        handleWeeklyNewsletter={handleWeeklyNewsletter}
        receiveWeeklyNewsletter={receiveWeeklyNewsletter}
        handleRealTimeNotification={handleRealTimeNotification}
        receiveRealTimeNotification={receiveRealTimeNotification}
        receiveRealTimeNotificationForNewPsqArqcSent={
          receiveRealTimeNotificationForNewPsqArqcSent
        }
        handleInternalReviewNotification={handleInternalReviewNotification}
        isDataSteward={isDataSteward}
        handleDataSteward={handleDataSteward}
        dataStewardCheckboxDisplay={dataStewardCheckboxDisplay}
        dataStewardValue={dataStewardValue}
        showDSError={showDSError}
      />
    </section>
  );
};

ClientAuthList.propTypes = {
  auths: PropTypes.array.isRequired,
  handleSearchChange: PropTypes.func,
  handleClearSearchText: PropTypes.func,
  clientUserSearchText: PropTypes.string,
  handleEditButtonClick: PropTypes.func,
  handleDeleteButtonClick: PropTypes.func,
  handleNewButtonClick: PropTypes.func,
  showAddClientAuthModal: PropTypes.bool,
  handleAddClientAuthModalAction: PropTypes.func,
  auth: PropTypes.object,
  toggleShowAddClientAuthModal: PropTypes.func,
  showEditClientAuthModal: PropTypes.bool,
  handleEditClientAuthModalAction: PropTypes.func,
  toggleShowEditClientAuthModal: PropTypes.func,
  addAuthMessageText: PropTypes.string,
  maxLimitMessageText: PropTypes.string,
  numberOfEngagementAdmins: PropTypes.number,
  validateDomain: PropTypes.func,
  validateDomainWithRole: PropTypes.func,
  clientAuthFormValues: PropTypes.object,
  handleAddClientAuthModalValidateAction: PropTypes.func,
  validatingUser: PropTypes.bool,
  myEyError: PropTypes.string,
  handleDailyNewsletter: PropTypes.func,
  receiveDailyNewsletter: PropTypes.bool,
  handleWeeklyNewsletter: PropTypes.func,
  receiveWeeklyNewsletter: PropTypes.bool,
  receiveRealTimeNotification: PropTypes.bool,
  handleRealTimeNotification: PropTypes.func,
  receiveRealTimeNotificationForNewPsqArqcSent: PropTypes.bool,
  handleInternalReviewNotification: PropTypes.func,
  handleOnRoleTypeChange: PropTypes.func,
  isDataSteward: PropTypes.bool,
  handleDataSteward: PropTypes.func,
  dataStewardCheckboxDisplay: PropTypes.bool,
  dataStewardValue: PropTypes.bool,
  showDSError: PropTypes.bool,
  clientId: PropTypes.number,
  parentComponent: PropTypes.string,
  handleGridChanges: PropTypes.func,
  isAuthorizedUserSearchBoxVisible: PropTypes.bool,
};

export default ClientAuthList;
